import { Fragment } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { Phone } from 'lucide-react';
import { toast } from 'sonner';
import { getRunColor } from './runColors';
import RegPlate from './RegPlate';

const ORANGE = '#ee7203';
const PURPLE = '#a855f7';
const MAX_GREEN = '#16a34a';

// A vehicle's maximum carry weight in tonnes (some older records hold kilograms).
const maxCarryTonnes = (vehicle) => {
  const raw = parseFloat(vehicle?.max_weight) || 0;
  const tonnes = raw > 1000 ? raw / 1000 : raw;
  return tonnes > 0 ? Number(tonnes.toFixed(2)) : null;
};

/** Compact vehicle overview card for the New Transport UI's vehicle selector list.
 * Also acts as an Unplanned Queue drop target (id `vehicle-${vehicle.id}`) — same
 * convention the classic Edit Transport board already uses, so dropping an
 * unplanned pill here works identically.
 *
 * Each card has a white header with an orange line under it, so it is clearly separate from the deliveries below it.
 * In the header: the registration on its own as a yellow number plate (nothing but the registration is inside the
 * plate), and — next to it, filling the same height as the plate — two lines: the driver's name and phone number
 * on top, the vehicle's MAXIMUM CARRY WEIGHT (green) underneath.
 *
 * Shows every delivery line by default. `maxLines` (a shared cap computed by
 * the parent once the whole list no longer fits the screen without
 * scrolling) trims the LONGEST cards first so every card ends up showing a
 * consistent line count — "+N more" expands just that one card back to full.
 *
 * Stops from different runs are flattened into one list but a coloured divider
 * (matching the same per-run colour used on the map/right panel) is inserted
 * wherever a new run's stops begin, so it's clear at a glance where Run 1 ends
 * and Run 2/3/etc. starts. */
const VehicleMiniCard = ({ vehicle, isSelected, onClick, moveModeActive, maxLines = Infinity, expanded = false, onToggleExpand }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `vehicle-${vehicle.id}` });

  const runGroups = [
    { runNumber: 1, entries: vehicle.entries || [] },
    ...(vehicle.runs || []).map((r, i) => ({ runNumber: r.run_number || i + 2, entries: r.entries || [] })),
  ];
  const allStops = [];
  const runBoundaries = new Set(); // flat-array indices where a NEW run's stops begin (never index 0)
  runGroups.forEach((g) => {
    if (!g.entries.length) return;
    if (allStops.length > 0) runBoundaries.add(allStops.length);
    g.entries.forEach((e) => allStops.push({ ...e, __runNumber: g.runNumber }));
  });
  const maxCarry = maxCarryTonnes(vehicle);
  const visibleCount = expanded ? allStops.length : Math.min(allStops.length, maxLines);
  const hiddenCount = allStops.length - visibleCount;

  return (
    <button
      ref={setNodeRef}
      data-testid={`transport-new-vehicle-card-${vehicle.id}`}
      onClick={onClick}
      className={`text-left rounded-xl p-2.5 backdrop-blur-xl border-2 transition-all w-full ${
        isSelected ? 'bg-white/95 border-orange-400 shadow-lg' : 'bg-white/70 border-white/40 hover:bg-white/85'
      } ${isOver ? 'ring-2 ring-purple-400 border-purple-400 bg-purple-50/90' : ''} ${
        moveModeActive && !isSelected ? 'border-green-400 hover:border-green-500' : ''
      }`}
    >
      {/* Header: white with an orange line under it. Reg plate on the left; next to it, two lines the same
          total height as the plate — the driver's name (+ phone number) on top, the vehicle's max carry
          weight underneath. */}
      <div
        className="-mx-2.5 -mt-2.5 mb-1.5 rounded-t-[10px] bg-white px-2.5 py-1.5"
        style={{ borderBottom: `2px solid ${ORANGE}` }}
        data-testid={`transport-new-vehicle-header-${vehicle.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <RegPlate registration={vehicle.vehicle_registration} size="md" data-testid={`transport-new-vehicle-plate-${vehicle.id}`} />
          {vehicle.off_road && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/80 text-white flex-shrink-0">OFF ROAD</span>}
          <div className="flex flex-col justify-center min-w-0 gap-[1px]">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[11px] font-bold leading-none text-slate-900 truncate min-w-0">{vehicle.driver_name || 'Unassigned'}</span>
              {vehicle.driver_phone && (
                <span
                  data-testid={`transport-new-vehicle-phone-${vehicle.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard?.writeText(vehicle.driver_phone);
                    toast.success('Phone number copied');
                  }}
                  className="flex items-center gap-0.5 text-[10px] leading-none font-semibold text-slate-500 hover:text-orange-600 flex-shrink-0 cursor-pointer"
                  title="Click to copy"
                >
                  <Phone size={9} />{vehicle.driver_phone}
                </span>
              )}
            </div>
            <div className="text-[9px] font-extrabold leading-none" style={{ color: MAX_GREEN }} data-testid={`transport-new-vehicle-max-${vehicle.id}`}>
              {maxCarry != null ? `Max carry ${maxCarry}T` : 'Max carry —'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        {allStops.slice(0, visibleCount).map((s, i) => (
          <Fragment key={i}>
            {runBoundaries.has(i) && (
              <div className="flex items-center gap-1 my-0.5" data-testid={`transport-new-vehicle-card-${vehicle.id}-run-divider-${s.__runNumber}`}>
                <div className="flex-1 h-px" style={{ backgroundColor: `${getRunColor(s.__runNumber)}80` }} />
                <span className="text-[9px] font-bold tracking-wide" style={{ color: getRunColor(s.__runNumber) }}>RUN {s.__runNumber}</span>
                <div className="flex-1 h-px" style={{ backgroundColor: `${getRunColor(s.__runNumber)}80` }} />
              </div>
            )}
            <div className="text-[11px] text-slate-600 truncate flex justify-between gap-1">
              <span className="truncate">{s.customer_name || 'Unnamed'}</span>
              <span className="font-medium ml-1 flex-shrink-0" style={{ color: PURPLE }}>{s.postcode}</span>
            </div>
          </Fragment>
        ))}
        {allStops.length === 0 && <div className="text-[11px] text-slate-400 italic">No entries</div>}
        {hiddenCount > 0 && (
          <span
            data-testid={`transport-new-vehicle-more-${vehicle.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleExpand?.(vehicle.id); }}
            className="text-[10px] text-orange-500 font-semibold hover:underline text-left"
          >
            +{hiddenCount} more
          </span>
        )}
      </div>
    </button>
  );
};

export default VehicleMiniCard;
